import { useEffect } from "react";
import { useLight } from "@/context/LightContext";

/**
 * "Catch the light" effect — when a card enters the middle band of the
 * viewport (the visually lit portion), it picks up a subtle gold rim/sheen.
 *
 * Cheap because IntersectionObserver fires only on threshold crossings —
 * no scroll listener, no per-frame work. Cards themselves handle the
 * transition via CSS on `.in-light`.
 */
const SELECTOR = ".tilt-card, [data-glow-catch]";

export default function LitCatchController() {
    const { activated } = useLight();

    useEffect(() => {
        if (!activated) return;

        const io = new IntersectionObserver(
            (entries) => {
                for (const e of entries) {
                    e.target.classList.toggle("in-light", e.isIntersecting);
                }
            },
            {
                // "central/lit portion" of the viewport
                rootMargin: "-22% 0px -22% 0px",
                threshold: 0.01,
            },
        );

        const observeAll = () => {
            const nodes = document.querySelectorAll(SELECTOR);
            nodes.forEach((n) => io.observe(n));
        };
        observeAll();

        // Catch cards that mount after initial render (framer-motion transitions)
        const mo = new MutationObserver(() => {
            observeAll();
        });
        mo.observe(document.body, { childList: true, subtree: true });

        return () => {
            io.disconnect();
            mo.disconnect();
        };
    }, [activated]);

    return null;
}
